/**
 * lx-contact-editor —— 新建 / 编辑联系人表单
 *
 * 以覆盖层形式出现（从底部升起），支持两种模式：
 *   新建：空白表单
 *   编辑：预填现有值
 *
 * 为什么表单也要做（既然 AI 能建联系人）：
 *   这是「输入栏打字」这条路的兜底。AI 没配好、网络不通、或者用户就是
 *   不想打字描述，都需要一条能手动完成的路径。两条路都真实可用，
 *   才谈得上"AI 是通用控制层"——否则它是唯一控制层，一旦挂了应用就废了。
 */

import { LxElement, define } from '@/core/base-element';
import { contactsRepo } from '@/data/repo.contacts';
import { popOverlay } from '@/core/ui-store';
import type { Contact } from '@/types/models';

export class LxContactEditor extends LxElement {
  private _editing: Contact | null = null;
  private _saving = false;
  private _error = '';

  /** 传入联系人进入编辑模式，传 null 为新建 */
  set contact(value: Contact | null) {
    this._editing = value;
    this.update();
  }

  get contact(): Contact | null {
    return this._editing;
  }

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-modal);
        display: flex;
        align-items: flex-end;
        justify-content: center;
      }

      .backdrop {
        position: absolute;
        inset: 0;
        background: var(--c-overlay);
        backdrop-filter: blur(4px);
        animation: fade var(--dur-base) var(--ease-standard);
      }

      /* 从底部升起的面板，符合移动端习惯 */
      .panel {
        position: relative;
        width: 100%;
        max-height: 88%;
        overflow-y: auto;
        background: var(--c-bg);
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        padding-bottom: env(safe-area-inset-bottom, 0px);
        animation: rise var(--dur-base) var(--ease-standard);
      }

      @keyframes fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes rise {
        from { transform: translateY(8%); opacity: .6; }
        to { transform: translateY(0); opacity: 1; }
      }

      header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--sp-4) var(--layout-content-pad-x) var(--sp-2);
      }

      .heading {
        font-size: var(--fs-lg);
        font-weight: var(--fw-medium);
        color: var(--c-fg);
      }

      .close {
        width: 32px;
        height: 32px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        color: var(--c-fg-muted);
      }

      .close:hover { background: var(--c-surface-2); color: var(--c-fg); }

      .close svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.8; }

      form {
        padding: var(--sp-2) var(--layout-content-pad-x) var(--sp-4);
        display: flex;
        flex-direction: column;
        gap: var(--sp-4);
      }

      .field { display: flex; flex-direction: column; gap: var(--sp-2); }

      label {
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        letter-spacing: 0.04em;
      }

      input {
        width: 100%;
        min-height: 44px;
        padding: var(--sp-2) var(--sp-3);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-md);
        background: var(--c-surface);
        color: var(--c-fg);
        font-size: var(--fs-md);
        transition: border-color var(--dur-fast) var(--ease-standard);
      }

      input:focus {
        outline: none;
        border-color: var(--c-accent);
      }

      input::placeholder { color: var(--c-fg-subtle); }

      .err {
        padding: var(--sp-2) var(--sp-3);
        border-radius: var(--radius-md);
        background: var(--c-danger-soft);
        color: var(--c-danger);
        font-size: var(--fs-sm);
      }

      .actions {
        display: flex;
        gap: var(--sp-3);
        margin-top: var(--sp-1);
      }

      .btn {
        flex: 1;
        min-height: 46px;
        border-radius: var(--radius-full);
        font-size: var(--fs-md);
        font-weight: var(--fw-medium);
        display: grid;
        place-items: center;
        transition: opacity var(--dur-fast) var(--ease-standard);
      }

      .btn:active { opacity: .72; }

      .btn.ghost {
        background: var(--c-surface-2);
        color: var(--c-fg);
      }

      .btn.primary {
        background: var(--c-primary);
        color: var(--c-primary-fg);
      }

      .btn:disabled { opacity: .5; cursor: not-allowed; }
    `;
  }

  protected render(): string {
    const e = this._editing;
    const isEdit = e !== null;

    return `
      <div class="backdrop" data-close></div>
      <div class="panel" role="dialog" aria-modal="true" aria-label="${isEdit ? '编辑联系人' : '新建联系人'}">
        <header>
          <h2 class="heading">${isEdit ? '编辑联系人' : '新建联系人'}</h2>
          <button class="close" type="button" data-close aria-label="关闭">
            <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" stroke-linecap="round"/></svg>
          </button>
        </header>

        <form data-form>
          <div class="field">
            <label for="f-name">姓名</label>
            <input id="f-name" name="name" type="text" autocomplete="off"
              placeholder="例如：王二" value="${this.esc(e?.name ?? '')}" />
          </div>

          <div class="field">
            <label for="f-remark">备注名</label>
            <input id="f-remark" name="remark" type="text" autocomplete="off"
              placeholder="选填，例如：产品-李静" value="${this.esc(e?.remark ?? '')}" />
          </div>

          <div class="field">
            <label for="f-signature">个性签名</label>
            <input id="f-signature" name="signature" type="text" autocomplete="off"
              placeholder="选填" value="${this.esc(e?.signature ?? '')}" />
          </div>

          ${this._error ? `<p class="err" role="alert">${this.esc(this._error)}</p>` : ''}

          <div class="actions">
            <button class="btn ghost" type="button" data-close>取消</button>
            <button class="btn primary" type="submit" ${this._saving ? 'disabled' : ''}>
              ${this._saving ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    `;
  }

  protected onMounted(): void {
    this.$$('[data-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    const form = this.$('[data-form]') as HTMLFormElement | null;
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.submit(form);
    });

    // 自动聚焦姓名框，省用户一次点击
    const nameInput = this.$('#f-name') as HTMLInputElement | null;
    if (nameInput && !this._editing) {
      queueMicrotask(() => nameInput.focus());
    }
  }

  private async submit(form: HTMLFormElement): Promise<void> {
    if (this._saving) return;

    const data = new FormData(form);
    const name = String(data.get('name') ?? '').trim();
    const remark = String(data.get('remark') ?? '').trim();
    const signature = String(data.get('signature') ?? '').trim();

    if (!name) {
      this._error = '姓名不能为空';
      this.update();
      return;
    }

    this._saving = true;
    this._error = '';
    this.update();

    try {
      if (this._editing) {
        await contactsRepo.update(this._editing.id, { name, remark, signature });
      } else {
        await contactsRepo.create({ name, remark, signature });
      }
      this.close();
    } catch (err) {
      this._saving = false;
      this._error = err instanceof Error ? err.message : '保存失败，请重试';
      this.update();
    }
  }

  private close(): void {
    popOverlay();
  }

  connectedCallback(): void {
    super.connectedCallback();
    // Esc 关闭
    this._onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    window.addEventListener('keydown', this._onKey);
  }

  private _onKey: ((e: KeyboardEvent) => void) | null = null;

  protected onUnmounted(): void {
    if (this._onKey) window.removeEventListener('keydown', this._onKey);
  }
}

define('lx-contact-editor', LxContactEditor);
